import React, { useEffect, useReducer } from "react";
import { Grid, Box, Button, InputBase } from "@mui/material";
import RegisteredEmployees from "./RegisteredEmployees";
import MainCard from "components/MainCard";
import { fetchRegisteredEmployees, isLoading } from "store/reducers/registeredEmployees";
import { dispatch, useSelector } from "store";
import ScrollX from "components/ScrollX";
import { PAGINATION } from "utils/constants/pagination";
import { get } from "lodash";
import { styled, alpha } from "@mui/material/styles";
import T from "utils/constants/T";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CashDAutocomplete from "components/common/CashDAutocomplete";
import { fetchDeductionTransactionBusinessUnitList } from "store/reducers/deductionTransactionFilter";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  border: "1px solid #d9d9d9",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));
const RegisteredEmployeesPage = () => {
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch
  );
  const { systemCode } = useSelector((state: any) => state.companyTabData);
  const companyId = get(selectedCompany, "_id", "");
  const registeredEmployees = useSelector((state) => state.registeredEmployees);
  const { businessUnitList } = useSelector(
    (state: any) => state.deductionTransactionFilter
  );
  const { employees, totalRecords, totalPages } = registeredEmployees;
  const { INITIAL_PAGE, ROWS_PER_PAGE } = PAGINATION;

  const [localState, setLocalState] = useReducer(
    (prevState: any, newState: any) => ({ ...prevState, ...newState }),
    {
      pageNumber: INITIAL_PAGE,
      rowsPerPage: ROWS_PER_PAGE,
      isLoading: false,
      employeeName: "",
      businessUnit: null,
    }
  );

  const { rowsPerPage, employeeName, businessUnit } = localState;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePageChange = (page: number) => {
    setLocalState({ pageNumber: page });
    dispatch(isLoading(true));

    dispatch(
      fetchRegisteredEmployees(
        page,
        rowsPerPage,
        companyId,
        employeeName,
        businessUnit?._id
      )
    );
    document.getElementsByClassName("MuiTableContainer-root")[0].scrollTop = 0;
  };

  const handleRowsPerPageChange = (event: any) => {
    const { value } = event.target;
    setLocalState({ pageNumber: INITIAL_PAGE, rowsPerPage: value });
    dispatch(isLoading(true));
    dispatch(
      fetchRegisteredEmployees(
        INITIAL_PAGE,
        value,
        companyId,
        employeeName,
        businessUnit?._id
      )
    );
  };

  useEffect(() => {
    setLocalState({
      pageNumber: INITIAL_PAGE,
      rowsPerPage: ROWS_PER_PAGE,
    });
  }, [companyId, INITIAL_PAGE, ROWS_PER_PAGE]);

  // clear filter values if company is changed
  useEffect(() => {
    setLocalState({ businessUnit: null });
  }, [companyId]);

  // fetch business unit ids if compnay is selected and company payroll is astute
  useEffect(() => {
    if (companyId) dispatch(fetchCompanyDetails(companyId));
    dispatch(
      fetchRegisteredEmployees(
        INITIAL_PAGE,
        rowsPerPage,
        companyId,
        employeeName,
        businessUnit?._id
      )
    );

    if (companyId && systemCode === T.ASTUTE.toUpperCase()) {
      dispatch(fetchDeductionTransactionBusinessUnitList(companyId));
    }
  }, [companyId, businessUnit, systemCode, employeeName, rowsPerPage, INITIAL_PAGE]);

  let debounceTimer: any = null;

  const handleEmployeeNameSearch = (employeeName: any) => {
    const { name, value } = employeeName.target;
    setLocalState({ [name]: value });
    clearTimeout(debounceTimer);
    setLocalState({ isLoading: false });

    if ((value?.length >= 3) || (value?.length === 0)) {
      setLocalState({ isLoading: true });
      debounceTimer = setTimeout(() => {
        setLocalState({ pageNumber: INITIAL_PAGE });
        dispatch(
          fetchRegisteredEmployees(
            INITIAL_PAGE,
            rowsPerPage,
            companyId,
            value
          )
        );
        setLocalState({ isLoading: false });
      }, 500);
    }
  };

  const onHandleBusinessUnitChange = (value: string) => {
    setLocalState({ businessUnit: value });
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MainCard>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box>
                <Button variant="contained">Registered</Button>
              </Box>
              <Box>
                <Button
                  component={Link}
                  to="/employees/unregistered"
                  variant="text"
                >
                  Unregistered
                </Button>
              </Box>
              <Box sx={{ marginLeft: "20px" }}>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    placeholder={T.EMPLOYEE}
                    name="employeeName"
                    value={employeeName}
                    inputProps={{ "aria-label": "search" }}
                    onChange={handleEmployeeNameSearch}
                  />
                </Search>
              </Box>
              {companyId && systemCode === T.ASTUTE.toUpperCase() && (
                <Box>
                  <CashDAutocomplete
                    placeholder={T.BUSINESS_UNIT}
                    listDetails={businessUnitList}
                    value={businessUnit}
                    getByLabelText={(option: any) => `${option.name}`}
                    onHandleChange={(event: any, newValue: any) =>
                      onHandleBusinessUnitChange(newValue)
                    }
                    fullWidth
                    width="200px"
                  />
                </Box>
              )}
            </Box>
            <ScrollX>
              <RegisteredEmployees
                employees={employees}
                page={localState.pageNumber}
                pageSize={localState.rowsPerPage}
                totalRecords={totalRecords}
                totalPages={totalPages}
                open={open}
                anchorEl={anchorEl}
                handleClick={handleClick}
                handleClose={handleClose}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                companyId={companyId}
                systemCode={systemCode}
              />
            </ScrollX>
          </MainCard>
        </Grid>
      </Grid>
    </>
  );
};

export default RegisteredEmployeesPage;
